// ─── Enums ────────────────────────────────────────────────────────────────────

export enum ExamType {
  StudentEvaluation = 'StudentEvaluation',
  TeacherEvaluation = 'TeacherEvaluation',
  InstructorEvaluation = 'InstructorEvaluation',
  MonographQuestionnaire = 'MonographQuestionnaire',
  Challenge = 'Challenge',
  Qualification = 'Qualification',
  Scholarship = 'Scholarship',
  Entrance = 'Entrance',
}

export enum ExamGradeCriteria {
  Last = 'Last',
  Highest = 'Highest',
}

export enum ExamReferenceShow {
  None = 'None',
  Partial = 'Partial',
  Total = 'Total',
}

export enum EQuestionType {
  SingleLineText = 'SingleLineText',
  MultiLineText = 'MultiLineText',
  Numeric = 'Numeric',
  SingleChoice = 'SingleChoice',
  MultipleChoice = 'MultipleChoice',
  Grade = 'Grade',
  Audio = 'Audio',
  Attachment = 'Attachment',
}

export enum ExamItemLevel {
  Simple = 1,
  Complex = 2,
}

export enum ExamItemStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

export enum ETranslationStatus {
  PendingTranslation = 0,
  Translated = 1,
  Reviewed = 2,
  Outdated = 3,
}

export enum EPublicationStatus {
  Active = 'Active',
  Closed = 'Closed',
  Scheduled = 'Scheduled',
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface Exam {
  readonly id: number;
  readonly title: string;
  readonly examType: ExamType;
  readonly disciplineId: number | null;
  readonly disciplineName: string | null;
  readonly gradeCriteria: ExamGradeCriteria;
  readonly weight: number;
  readonly examReferenceShow: ExamReferenceShow;
  readonly examReferenceShowPost: ExamReferenceShow;
  readonly showAnswersTries: boolean;
  readonly randomize: boolean;
  readonly questionsQuantity: number | null;
  readonly percentualComplexity: number;
  readonly recommendedToDoOnPortal: boolean;
  readonly enableSafeEvaluationMode: boolean;
  readonly maxScreenExitsAllowed: number;
  readonly enableBlockUnauthorizedActions: boolean;
  readonly active: boolean;
  readonly questionsCount: number;
  readonly languagesCount: number;
  readonly createdAt: string;
}

export interface ExamFormData {
  readonly title: string;
  readonly instruction: string;
  /** Chave numérica retornada por /api/v1/exam/exam-types */
  readonly examType: number | null;
  /** ID da empresa (opcional) — chave de /api/v1/exam/company-dropdown */
  readonly companyId: number | null;
  readonly companyName: string | null;
  readonly disciplineId: number | null;
  readonly disciplineName: string | null;
  /** Chave numérica retornada por /api/v1/exam/exam-grade-criteria */
  readonly gradeCriteria: number | null;
  readonly weight: number;
  /** Chave numérica retornada por /api/v1/exam/exam-reference-show */
  readonly examReferenceShow: number | null;
  /** Chave numérica retornada por /api/v1/exam/exam-reference-show */
  readonly examReferenceShowPost: number | null;
  readonly showAnswersTries: boolean;
  readonly randomize: boolean;
  readonly questionsQuantity: number | null;
  readonly percentualComplexity: number;
  readonly recommendedToDoOnPortal: boolean;
  readonly enableSafeEvaluationMode: boolean;
  readonly maxScreenExitsAllowed: number;
  readonly enableBlockUnauthorizedActions: boolean;
}

export interface ExamLang {
  readonly id: number;
  readonly examId: number;
  readonly cultureInfo: string;
  readonly instruction: string;
  readonly active: boolean;
  readonly questionsCount: number;
}

export interface ExamItemOption {
  readonly id: number;
  readonly label: string;
  readonly isCorrect: boolean;
  readonly order: number;
}

export interface ExamItem {
  readonly id: number;
  readonly examLanguageId: number;
  readonly componentType: EQuestionType;
  readonly label: string;
  readonly labelClean: string;
  readonly cultureInfo: string;
  readonly weight: number;
  readonly required: boolean;
  readonly fixed: boolean;
  readonly pageBreak: boolean;
  readonly randomizeAlternatives: boolean;
  readonly correctAnswer: string | null;
  readonly min: number | null;
  readonly max: number | null;
  readonly decimalPlaces: number | null;
  readonly showLabel: boolean;
  readonly information: string | null;
  readonly showInformation: boolean;
  readonly isTemplate: boolean;
  readonly level: ExamItemLevel;
  readonly status: ExamItemStatus;
  readonly cancelled: boolean;
  readonly cancelJustification: string | null;
  readonly disciplineId: number | null;
  readonly disciplineName: string | null;
  readonly options: readonly ExamItemOption[];
  readonly order: number;
  readonly parentQuestionId: number | null;
  readonly translationStatus: ETranslationStatus | null;
  readonly translatedAt: string | null;
  readonly reviewedAt: string | null;
}

export interface QuestionTranslation {
  readonly questionId: number;
  readonly parentQuestionId: number;
  readonly cultureInfo: string;
  readonly label: string;
  readonly options: readonly ExamItemOption[];
  readonly explanation: string;
  readonly translationStatus: ETranslationStatus;
  readonly translatedAt: string | null;
  readonly reviewedAt: string | null;
}

export interface LessonPlan {
  readonly id: number;
  readonly title: string;
  readonly disciplineId: number;
  readonly disciplineName: string;
  readonly classGroup: string;
  readonly professorName: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: 'active' | 'closed' | 'scheduled';
}

export interface ExamPublication {
  readonly id: number;
  readonly examId: number;
  readonly lessonPlanId: number;
  readonly lessonPlanTitle: string;
  readonly classGroup: string;
  readonly professorName: string;
  readonly disciplineName: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: EPublicationStatus;
  readonly maxAttempts: number;
  readonly durationMinutes: number;
  readonly notifyStudents: boolean;
  readonly allowExtraAttempt: boolean;
  readonly teacherEvalPublished: boolean;
  readonly studentsResponded: number;
  readonly totalStudents: number;
  readonly averageGrade: number | null;
  readonly students: readonly ExamPublishStudent[];
  readonly stats: ExamPublishStats;
}

export interface ExamPublishStudent {
  readonly id: number;
  readonly personId: number;
  readonly personName: string;
  readonly personCpf: string;
  readonly personEmail: string;
  readonly attempts: number;
  readonly bestGrade: number | null;
  readonly lastGrade: number | null;
  readonly lastAttemptDate: string | null;
}

export interface ExamPublishStats {
  readonly totalStudents: number;
  readonly completedCount: number;
  readonly pendingCount: number;
  readonly averageGrade: number | null;
  readonly highestGrade: number | null;
  readonly lowestGrade: number | null;
  readonly pendingCorrectionCount: number;
}

export interface ExamFilters {
  readonly search: string;
  readonly type: string;
  readonly discipline: string;
  readonly status: string;
  /** Chave numérica de /api/v1/exam/class-room-disciplines-status, ou 'all' */
  readonly classRoomDisciplineStatus: string;
  readonly page: number;
  /** Incrementado a cada clique em Pesquisar para forçar nova request */
  readonly searchTrigger: number;
}

export interface Discipline {
  readonly id: number;
  readonly name: string;
}
